import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronDown, ExternalLink, KeyRound, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { accounts } from '../../services/apiService';
import { Loader as SectionLoader } from '../UI/LoadingSpinner';

/**
 * Компонент для отображения AccountCredentials, назначенных пользователю.
 * Аккаунты — сворачиваемые accordion. Внутри каждого — группировка по инструменту (tool).
 */
const AssignedCredentialsTab = ({
  userId,
  isSelf = true,
  credentialsList: externalCredentialsList,
  loading: externalLoading,
  error: externalError
}) => {
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [visibleValues, setVisibleValues] = useState({});

  const isExternalData = externalCredentialsList !== undefined;

  const {
    data: credentialsResponse,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['assignedCredentials', userId, isSelf ? 'self' : 'admin'],
    queryFn: () => isSelf
      ? accounts.getAssignedCredentialsSelf(userId)
      : accounts.getAssignedCredentialsByUserId(userId),
    retry: 1,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
    enabled: !!userId && !isExternalData
  });

  // Data shape: [{ owner_type, owner_id, account_name, account_login, sets: [{ id, tool_id, name, url, fields: [{ field_key, value }] }], credentials: [] }]
  const accountGroups = isExternalData
    ? (externalCredentialsList || [])
    : (credentialsResponse?.data?.data || credentialsResponse?.data || []);

  const displayLoading = isExternalData ? (externalLoading ?? false) : isLoading;
  const displayError = isExternalData ? externalError : (isError ? error?.message : null);

  const toggleAccount = (ownerId) => {
    setExpandedAccounts(prev => ({ ...prev, [ownerId]: !prev[ownerId] }));
  };

  const toggleValueVisibility = (key) => {
    setVisibleValues(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const [copiedKeys, setCopiedKeys] = useState({});
  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKeys(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopiedKeys(prev => ({ ...prev, [key]: false })), 2000);
    });
  };


  if (displayLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <SectionLoader variant="component" size="md" fullHeight={false} />
      </div>
    );
  }

  if (displayError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
        <p className="text-light-text-secondary dark:text-dark-text-secondary">
          {displayError || 'Failed to load assigned credentials'}
        </p>
      </div>
    );
  }

  const totalFields = accountGroups.reduce((sum, g) =>
    sum + (g.sets || []).reduce((s, set) => s + (set.fields?.length || 0), 0), 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
          Assigned Account Credentials
        </h3>
        <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
          {totalFields} field{totalFields !== 1 ? 's' : ''} across {accountGroups.length} account{accountGroups.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Empty State */}
      {accountGroups.length === 0 && (
        <div className="text-center py-12 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg border border-light-border-primary dark:border-dark-border-primary">
          <KeyRound className="mx-auto h-12 w-12 text-light-text-tertiary dark:text-dark-text-tertiary mb-4" />
          <p className="text-light-text-secondary dark:text-dark-text-secondary text-lg mb-2">
            No assigned credentials found
          </p>
          <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
            You have no account credentials assigned to you yet
          </p>
        </div>
      )}

      {/* Account accordion groups */}
      {accountGroups.map((group, index) => {
        const ownerId = group.owner_id ?? index;
        const isExpanded = !!expandedAccounts[ownerId];
        const sets = group.sets || [];

        return (
          <div
            key={ownerId}
            className="relative bg-gradient-to-br from-light-bg-primary to-light-bg-secondary dark:from-dark-bg-tertiary dark:to-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
            style={{ animationDelay: `${index * 30}ms` }}
          >
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 via-purple-500 to-pink-500" />

            {/* Accordion header */}
            <button
              onClick={() => toggleAccount(ownerId)}
              className="flex items-center gap-3 w-full px-5 py-4 text-left hover:bg-light-bg-tertiary/40 dark:hover:bg-dark-bg-primary/40 transition-colors duration-200"
            >
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 flex items-center justify-center flex-shrink-0">
                <KeyRound className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-light-text-primary dark:text-dark-text-primary truncate">
                  {group.account_name || group.account_login || `Account #${ownerId}`}
                </div>
                {group.account_name && group.account_login && (
                  <div className="mt-0.5">
                    <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                      {group.account_login}
                    </span>
                  </div>
                )}
              </div>

              <ChevronDown className={`h-5 w-5 ml-1 text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
            </button>

            {/* Expandable body */}
            <div
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{ maxHeight: isExpanded ? '2000px' : '0px', opacity: isExpanded ? 1 : 0 }}
            >
              <div className="border-t border-light-border-primary/30 dark:border-dark-border-primary/30">
                {sets.map((set, setIdx) => (
                  <div key={set.id ?? setIdx} className={setIdx > 0 ? 'border-t border-light-border-primary dark:border-dark-border-primary' : ''}>
                    {/* Set header */}
                    <div className="flex min-w-0 flex-wrap items-center gap-3 px-4 py-3 bg-light-bg-tertiary/60 dark:bg-dark-bg-primary/40 sm:px-5">
                      <KeyRound className="h-4 w-4 text-primary-500 dark:text-primary-400 flex-shrink-0" />
                      <span className="min-w-0 flex-1 break-words font-semibold text-sm text-light-text-primary dark:text-dark-text-primary">
                        {set.name || `Set #${set.id}`}
                      </span>
                      {set.url && (
                        <a
                          href={set.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-secondary hover:text-primary-500 dark:hover:bg-dark-bg-secondary dark:hover:text-primary-400 transition-colors"
                          title="Open link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                      <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary sm:ml-auto">
                        {set.fields?.length || 0} field{set.fields?.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Fields */}
                    <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
                      {(set.fields || []).map((field, fIdx) => {
                        const visKey = `${set.id}_${field.field_key}_${fIdx}`
                        return (
                          <div key={visKey} className="flex flex-col items-stretch gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-5">
                            <span className="min-w-0 break-words text-sm text-light-text-secondary dark:text-dark-text-secondary sm:w-36 sm:flex-shrink-0">
                              {field.field_key}
                            </span>
                            <div className="flex-1 min-w-0">
                              {field.value ? (
                                <div className="flex min-w-0 items-center gap-1 bg-light-bg-primary dark:bg-dark-bg-primary border border-light-border-primary dark:border-dark-border-primary rounded-lg px-2 py-1 sm:gap-2 sm:px-3 sm:py-2">
                                  <span className="min-w-0 flex-1 break-all text-sm font-mono text-light-text-primary dark:text-dark-text-primary sm:truncate">
                                    {visibleValues[visKey] ? field.value : '•'.repeat(Math.min(field.value.length, 16))}
                                  </span>
                                  <button
                                    onClick={() => toggleValueVisibility(visKey)}
                                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-secondary hover:text-light-text-primary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-text-primary transition-colors"
                                    title={visibleValues[visKey] ? 'Hide' : 'Show'}
                                  >
                                    {visibleValues[visKey] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                  </button>
                                  <button
                                    onClick={() => handleCopy(field.value, visKey)}
                                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-secondary hover:text-light-text-primary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-text-primary transition-colors"
                                    title="Copy value"
                                  >
                                    {copiedKeys[visKey] ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center bg-light-bg-primary dark:bg-dark-bg-primary border border-dashed border-light-border-primary dark:border-dark-border-primary rounded-lg px-3 py-2">
                                  <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary italic">Not set</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AssignedCredentialsTab;
